import type {ProductDetail, ProductVolumeVariant} from "./api/types";

function extractVolumeLabel(name: string): string {
  const parts = name.split(",").map((s) => s.trim()).filter(Boolean);
  if(parts.length >= 2) {
    const last = parts[parts.length - 1];
    if(/^\d+(?:\.\d+)?\s*[a-zA-Z]{1,6}$/u.test(last)) return last;
  }
  const match = name.match(/(\d+(?:\.\d+)?)\s*(ml|g|gr|grams?|kg|l|lt|cl|oz|fl\s*oz)\b/ui);
  if(match) return `${match[1]} ${match[2]}`.replace(/\s+/g, " ");
  if(parts.length >= 2) return parts[parts.length - 1];
  return name.trim();
}

function sortKey(label: string): number {
  const match = label.match(/(\d+(?:\.\d+)?)\s*(ml|g|gr|grams?|kg|l|lt|cl|oz|fl\s*oz)?/ui);
  if(!match) return 0;
  const amount = parseFloat(match[1]);
  const unit = (match[2] || "ml").toLowerCase();
  if(unit === "l" || unit === "lt") return amount * 1000;
  if(unit === "cl") return amount * 10;
  if(unit === "kg") return amount * 1000;
  return amount;
}

/** Prefer API volumeVariants; fall back to brand-line variants when API returns none. */
export function resolvePdpVolumeVariants(product: ProductDetail): ProductVolumeVariant[] {
  if(product.volumeVariants?.length >= 2) {
    return product.volumeVariants;
  }
  const variants = product.variants || [];
  if(variants.length < 2) {
    return product.volumeVariants || [];
  }

  const byLabel = new Map<string, ProductVolumeVariant & {_sortKey: number}>();
  for(const v of variants) {
    const label = extractVolumeLabel(v.nume);
    if(!label) continue;
    const key = label.toLowerCase().replace(/\s+/g, " ");
    const entry: ProductVolumeVariant & {_sortKey: number} = {
      id: v.id,
      slug: v.slug || String(v.id),
      url: v.url || `/p/${v.slug || v.id}`,
      label,
      price: v.pretUnit,
      oldPrice: v.discountPercent > 0 && (v.pretBase ?? 0) > v.pretUnit ? (v.pretBase ?? null) : null,
      inStock: v.inStock,
      isCurrent: v.id === product.id || v.active,
      kind: "regular",
      _sortKey: sortKey(label)
    };
    const existing = byLabel.get(key);
    if(!existing || entry.isCurrent || (entry.inStock && !existing.inStock)) {
      byLabel.set(key, entry);
    }
  }

  const resolved = Array.from(byLabel.values())
    .sort((a, b) => a._sortKey - b._sortKey || a.id - b.id)
    .map(({_sortKey, ...row}) => row);

  if(resolved.length >= 2) return resolved;
  return product.volumeVariants || [];
}
